import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../utils/apiConfig';
import {
    ArrowLeft,
    RefreshCw,
    Copy,
    ExternalLink,
    Users,
    Wallet,
    Clock,
    CheckCircle,
    AlertCircle,
    DollarSign,
    Activity,
    Settings,
    Eye,
    EyeOff
} from 'lucide-react';

const WalletServiceTypeDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [walletServiceType, setWalletServiceType] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [showWebhookSecret, setShowWebhookSecret] = useState(false);

    // Fetch wallet service type details
    const fetchWalletServiceType = async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${API_BASE_URL}/wallet-service-types/view/${id}`,
                {
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setWalletServiceType(data.data);
                setError(null);
            } else {
                throw new Error(data.message || 'Failed to fetch wallet service type details');
            }
        } catch (err) {
            console.error('Error fetching wallet service type:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        if (id) {
            fetchWalletServiceType();
        }
    }, [id]);

    // Refresh data
    const refreshData = async () => {
        setIsRefreshing(true);
        await fetchWalletServiceType();
        setTimeout(() => setIsRefreshing(false), 500);
    };

    // Copy to clipboard
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        // You could add a toast notification here
    };

    // Get status color
    const getStatusColor = (isActive) => {
        return isActive
            ? 'bg-green-100 text-green-700 border-green-300'
            : 'bg-gray-100 text-gray-600 border-gray-300';
    };

    // Get status text
    const getStatusText = (isActive) => {
        return isActive ? 'ACTIVE' : 'INACTIVE';
    };

    // Open blockchain explorer
    const openBlockchainExplorer = (address, network) => {
        let url = '';
        if (network?.includes('TRC20') || network?.includes('TRON')) {
            url = `https://tronscan.org/#/address/${address}`;
        } else if (network?.includes('ERC20') || network?.includes('ETH')) {
            url = `https://etherscan.io/address/${address}`;
        }

        if (url) {
            window.open(url, '_blank');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600">Loading wallet service type details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
                    <p className="text-red-600 mb-4">Error: {error}</p>
                    <button
                        onClick={refreshData}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (!walletServiceType) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Wallet service type not found</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/wallet-service-types')}
                                className="text-gray-600 hover:text-gray-800 transition-colors"
                            >
                                <ArrowLeft className="w-6 h-6" />
                            </button>
                            <div>
                                <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
                                    Wallet Service Details
                                </h1>
                                <p className="text-gray-600 text-sm md:text-base">
                                    View detailed information about this wallet service
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={refreshData}
                            disabled={isRefreshing}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 flex items-center space-x-2 shadow-lg hover:shadow-blue-500/25 disabled:opacity-50"
                        >
                            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Information */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* User Information Card */}
                        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <Users className="w-6 h-6 text-blue-600" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-800">User Information</h2>
                                    <p className="text-gray-600">Basic details about the user</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                                    <div className="bg-gray-50 rounded-lg p-3 text-gray-800">
                                        {walletServiceType.userInfo.fullName || 'N/A'}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">User ID</label>
                                    <div className="bg-gray-50 rounded-lg p-3 text-gray-800 font-mono">
                                        {walletServiceType.userId}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                                    <div className="bg-gray-50 rounded-lg p-3 text-gray-800">
                                        {walletServiceType.userInfo.email}
                                    </div>
                                </div>

                                {walletServiceType.userInfo.phone && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                                        <div className="bg-gray-50 rounded-lg p-3 text-gray-800">
                                            {walletServiceType.userInfo.phone}
                                        </div>
                                    </div>
                                )}

                                {walletServiceType.description && (
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                        <div className="bg-gray-50 rounded-lg p-3 text-gray-800">
                                            {walletServiceType.description}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Wallets Information */}
                        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                                    <Wallet className="w-6 h-6 text-green-600" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-800">Wallet Information</h2>
                                    <p className="text-gray-600">Blockchain wallets and balances</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                {walletServiceType.wallets && walletServiceType.wallets.length > 0 ? (
                                    walletServiceType.wallets.map((wallet, index) => (
                                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                                        <Wallet className="w-4 h-4 text-blue-600" />
                                                    </div>
                                                    <div>
                                                        <h3 className="font-medium text-gray-800">{wallet.walletNetwork}</h3>
                                                        <p className="text-sm text-gray-600">Blockchain wallet</p>
                                                    </div>
                                                </div>
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${wallet.isActive ? 'bg-green-100 text-green-700 border-green-300' : 'bg-gray-100 text-gray-600 border-gray-300'
                                                    }`}>
                                                    {wallet.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Wallet Address</label>
                                                    <div className="flex items-center space-x-2">
                                                        <code className="flex-1 bg-gray-50 rounded-lg p-3 text-sm text-gray-800 font-mono break-all">
                                                            {wallet.walletAddress || 'N/A'}
                                                        </code>
                                                        {wallet.walletAddress && (
                                                            <>
                                                                <button
                                                                    onClick={() => copyToClipboard(wallet.walletAddress)}
                                                                    className="text-gray-400 hover:text-gray-600 transition-colors p-2"
                                                                    title="Copy address"
                                                                >
                                                                    <Copy className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() => openBlockchainExplorer(wallet.walletAddress, wallet.walletNetwork)}
                                                                    className="text-blue-600 hover:text-blue-700 transition-colors p-2"
                                                                    title="View on blockchain explorer"
                                                                >
                                                                    <ExternalLink className="w-4 h-4" />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Total Balance</label>
                                                    <div className="bg-gray-50 rounded-lg p-3">
                                                        <div className="text-lg font-semibold text-gray-800">
                                                            {wallet.totalDepositAmount?.toFixed(2) || '0.00'} USDT
                                                        </div>
                                                        <div className="text-sm text-gray-600">
                                                            From {wallet.totalDeposits || 0} deposits
                                                        </div>
                                                    </div>
                                                </div>

                                                {wallet.lastDepositAmount > 0 && (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Last Deposit</label>
                                                        <div className="bg-green-50 rounded-lg p-3">
                                                            <div className="text-lg font-semibold text-green-600">
                                                                +{wallet.lastDepositAmount} USDT
                                                            </div>
                                                            {wallet.lastDepositAt && (
                                                                <div className="text-sm text-gray-600">
                                                                    {new Date(wallet.lastDepositAt).toLocaleString()}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {wallet.walletGeneratedAt && (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Generated</label>
                                                        <div className="bg-gray-50 rounded-lg p-3 text-gray-800">
                                                            {new Date(wallet.walletGeneratedAt).toLocaleString()}
                                                        </div>
                                                    </div>
                                                )}

                                                {wallet.lastTransactionHash && (
                                                    <div className="md:col-span-2">
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Last Transaction Hash</label>
                                                        <div className="flex items-center space-x-2">
                                                            <code className="flex-1 bg-gray-50 rounded-lg p-3 text-sm text-gray-800 font-mono break-all">
                                                                {wallet.lastTransactionHash}
                                                            </code>
                                                            <button
                                                                onClick={() => copyToClipboard(wallet.lastTransactionHash)}
                                                                className="text-gray-400 hover:text-gray-600 transition-colors p-2"
                                                                title="Copy transaction hash"
                                                            >
                                                                <Copy className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center text-gray-500 py-8">
                                        <Wallet className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                        <p>No wallets configured</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Status Card */}
                        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                    <Activity className="w-5 h-5 text-purple-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800">Status</h3>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Current Status</label>
                                    <span className={`inline-flex px-3 py-2 rounded-full text-sm font-medium border ${getStatusColor(walletServiceType.isActive)}`}>
                                        {getStatusText(walletServiceType.isActive)}
                                    </span>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Created</label>
                                    <div className="bg-gray-50 rounded-lg p-3 text-gray-800">
                                        {new Date(walletServiceType.createdAt).toLocaleString()}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Updated</label>
                                    <div className="bg-gray-50 rounded-lg p-3 text-gray-800">
                                        {new Date(walletServiceType.updatedAt).toLocaleString()}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Configuration Card */}
                        {/* {walletServiceType.configuration && (
                            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                                <div className="flex items-center space-x-3 mb-4">
                                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                        <Settings className="w-5 h-5 text-orange-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-800">Configuration</h3>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    {walletServiceType.configuration.minAmount !== undefined && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Min Amount</label>
                                            <div className="bg-gray-50 rounded-lg p-3 text-gray-800">
                                                {walletServiceType.configuration.minAmount} USDT
                                            </div>
                                        </div>
                                    )}

                                    {walletServiceType.configuration.maxAmount !== undefined && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Max Amount</label>
                                            <div className="bg-gray-50 rounded-lg p-3 text-gray-800">
                                                {walletServiceType.configuration.maxAmount} USDT
                                            </div>
                                        </div>
                                    )}

                                    {walletServiceType.configuration.feePercentage !== undefined && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Fee Percentage</label>
                                            <div className="bg-gray-50 rounded-lg p-3 text-gray-800">
                                                {walletServiceType.configuration.feePercentage}%
                                            </div>
                                        </div>
                                    )}

                                    {walletServiceType.configuration.autoApprove !== undefined && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Auto Approve</label>
                                            <div className="bg-gray-50 rounded-lg p-3 text-gray-800">
                                                {walletServiceType.configuration.autoApprove ? 'Enabled' : 'Disabled'}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )} */}

                        {/* Webhook Information */}
                        {/* {(walletServiceType.callbackUrl || walletServiceType.webhookSecret) && (
                            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                                <div className="flex items-center space-x-3 mb-4">
                                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                                        <Activity className="w-5 h-5 text-indigo-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-800">Webhook</h3>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    {walletServiceType.callbackUrl && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Callback URL</label>
                                            <div className="bg-gray-50 rounded-lg p-3 text-gray-800 break-all">
                                                {walletServiceType.callbackUrl}
                                            </div>
                                        </div>
                                    )}

                                    {walletServiceType.webhookSecret && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Webhook Secret</label>
                                            <div className="flex items-center space-x-2">
                                                <div className="flex-1 bg-gray-50 rounded-lg p-3 text-gray-800 font-mono">
                                                    {showWebhookSecret ? walletServiceType.webhookSecret : '***************'}
                                                </div>
                                                <button
                                                    onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                                                    className="text-gray-400 hover:text-gray-600 transition-colors p-2"
                                                    title={showWebhookSecret ? 'Hide secret' : 'Show secret'}
                                                >
                                                    {showWebhookSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )} */}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WalletServiceTypeDetailPage;